import { convertPrice, PriceTier, UomLadder } from 'utils/price-units';

export interface PriceDelta {
  /** Percentage change, or null when the two prices are not comparable. */
  pct: number | null;
  comparable: boolean;
  /** Why not, when comparable is false. Rendered as a hint, not an error. */
  reason?: 'no-current' | 'no-incoming' | 'different-unit' | 'zero-current';
  /** The incoming price restated on the CURRENT cell's rung, for the tooltip. */
  incomingOnCurrentTier?: number;
}

/**
 * Percentage change between the supplier's current cell and the incoming row.
 *
 * The whole point of this helper is that it REFUSES to compare across rungs it
 * cannot convert. A naive `(next - current) / current` is the most likely
 * silent correctness bug in the whole review screen: if the incoming price is
 * per carton and the stored cell is per kilo, a 12x ladder difference renders
 * as a confident, authoritative "+1100%" and an operator reasonably concludes
 * the supplier has gone mad.
 *
 * convertPrice already returns null when a ladder hop (packSize / netContent)
 * is unknown, and that null is honoured here rather than coerced — an
 * unconvertible pair is reported as not comparable so the cell can render a
 * dash and a hint.
 */
export function priceDelta(
  incoming: number | null | undefined,
  incomingTier: PriceTier | null | undefined,
  current: number | null | undefined,
  currentTier: PriceTier | null | undefined,
  ladder: UomLadder
): PriceDelta {
  if (incoming == null || !Number.isFinite(incoming)) {
    return { pct: null, comparable: false, reason: 'no-incoming' };
  }

  // No existing cell is not a problem — it is a new price, and the review grid
  // shows it as such rather than as a change.
  if (current == null || !Number.isFinite(current)) {
    return { pct: null, comparable: false, reason: 'no-current' };
  }

  if (current === 0) {
    return { pct: null, comparable: false, reason: 'zero-current' };
  }

  // A missing rung on either side means we do not know what either number
  // measures. Guessing is exactly the failure this helper exists to prevent.
  if (!incomingTier || !currentTier) {
    return { pct: null, comparable: false, reason: 'different-unit' };
  }

  const restated = convertPrice(incoming, incomingTier, currentTier, ladder);

  if (restated == null || !Number.isFinite(restated)) {
    return { pct: null, comparable: false, reason: 'different-unit' };
  }

  return {
    pct: ((restated - current) / current) * 100,
    comparable: true,
    incomingOnCurrentTier: restated,
  };
}

/** Percent formatted for a pill: signed, one decimal, no trailing ".0". */
export function formatDeltaPct(pct: number): string {
  const rounded = Math.round(pct * 10) / 10;
  const sign = rounded > 0 ? '+' : '';

  return `${sign}${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}%`;
}
